import React from 'react';
import { getItem } from 'smart-react';
import {
  AlertTriangle,
  CheckCircle2,
  Info,
  RefreshCw,
  X,
  XCircle,
} from 'lucide-react';
import { EVENTS_DATA_TYPES } from '../../../../constants/eventDataTypes';
import {
  APP_CONFIG,
  AUTH_TOKENS,
  DisplayNotification,
  NOTIFICATION_KEY,
} from '../../../../constants/applicationConstants';
import { syncSessionStorage, syncStorage } from '../../../../Utils/Storage/storageUtils';

const TOAST_VISIBILITY = new Set([
  DisplayNotification.SHOW,
  DisplayNotification.ALERT,
]);
const SERVICE_WORKER_RELOAD_EVENT = 'RELOAD';

const baseNotificationIconClassName =
  'h-5 w-5 shrink-0 rounded-full p-0.5 shadow-sm';

const notificationTheme = {
  success: {
    accent: 'border-emerald-500/60',
    badge: 'bg-emerald-500/12 text-emerald-700 dark:text-emerald-300',
    icon: CheckCircle2,
    iconClassName: `${baseNotificationIconClassName} bg-emerald-500/12 text-emerald-600 dark:bg-emerald-400/12 dark:text-emerald-300`,
  },
  warning: {
    accent: 'border-amber-500/60',
    badge: 'bg-amber-500/12 text-amber-700 dark:text-amber-300',
    icon: AlertTriangle,
    iconClassName: `${baseNotificationIconClassName} bg-amber-500/12 text-amber-600 dark:bg-amber-400/12 dark:text-amber-300`,
  },
  error: {
    accent: 'border-rose-500/60',
    badge: 'bg-rose-500/12 text-rose-700 dark:text-rose-300',
    icon: XCircle,
    iconClassName: `${baseNotificationIconClassName} bg-rose-500/12 text-rose-600 dark:bg-rose-400/12 dark:text-rose-300`,
  },
  info: {
    accent: 'border-primary/70',
    badge: 'bg-primary/12 text-primary',
    icon: Info,
    iconClassName: `${baseNotificationIconClassName} bg-primary/12 text-primary`,
  },
  none: {
    accent: 'border-border',
    badge: 'bg-muted text-muted-foreground',
    icon: RefreshCw,
    iconClassName: `${baseNotificationIconClassName} bg-muted text-muted-foreground`,
  },
};

const getStoredNotifications = () => {
  const notifications = getItem(NOTIFICATION_KEY);
  return Array.isArray(notifications) ? notifications : [];
};

const persistNotifications = (notifications) => {
  try {
    localStorage.setItem(NOTIFICATION_KEY, JSON.stringify(notifications));
    window.dispatchEvent(new Event('storage'));
  } catch {
    // Keep UI functional even if storage is unavailable.
  }
};

const markNotificationRead = (notificationId) => {
  if (notificationId === undefined || notificationId === null) return;

  const updatedNotifications = getStoredNotifications().map((item) =>
    item.notificationId === notificationId ? { ...item, isRead: true } : item
  );

  persistNotifications(updatedNotifications);
};

const resolveNotificationCallback = (callBack) => {
  if (!callBack) return null;
  if (typeof callBack === 'function') return callBack;
  if (typeof callBack !== 'string') return null;

  try {
    // eslint-disable-next-line no-eval
    return eval(`(${callBack})`);
  } catch {
    return null;
  }
};

const mergeNotificationIntoStore = (notification, existingNotification) => {
  const storedNotifications = getStoredNotifications();
  let updatedNotifications;

  if (existingNotification) {
    updatedNotifications = storedNotifications.some(
      (item) => item.notificationId === existingNotification.notificationId
    )
      ? storedNotifications.map((item) =>
          item.notificationId === existingNotification.notificationId
            ? { ...existingNotification }
            : item
        )
      : [...storedNotifications, existingNotification];
  } else {
    const existingIndex = storedNotifications.findIndex(
      (item) => item.notificationId === notification.notificationId
    );

    if (existingIndex === -1) {
      updatedNotifications = [...storedNotifications, notification];
    } else {
      updatedNotifications = storedNotifications.map((item, index) =>
        index === existingIndex ? { ...item, ...notification } : item
      );
    }
  }

  persistNotifications(updatedNotifications);
};

const updateNotificationsState = (currentNotifications, incomingNotification) => {
  const duplicateIndex = currentNotifications.findIndex(
    (item) =>
      item.title === incomingNotification.title &&
      item.description === incomingNotification.description
  );

  if (duplicateIndex === -1) {
    const nextNotification = {
      count: incomingNotification.count ?? 0,
      isNotify: incomingNotification.isNotify ?? true,
      ...incomingNotification,
    };

    mergeNotificationIntoStore(nextNotification);
    return [...currentNotifications, nextNotification];
  }

  const nextNotifications = [...currentNotifications];
  const existingNotification = { ...nextNotifications[duplicateIndex] };

  if (!existingNotification.isNotify && incomingNotification.isNotify) {
    existingNotification.isNotify = true;
    existingNotification.count = 0;
  } else {
    const currentCount = existingNotification.count ?? 0;
    existingNotification.count = currentCount === 0 ? 2 : currentCount + 1;
  }

  nextNotifications[duplicateIndex] = existingNotification;
  mergeNotificationIntoStore(incomingNotification, existingNotification);

  return nextNotifications;
};

const AppNotifications = () => {
  const [notifications, setNotifications] = React.useState([]);
  const autoCloseTimersRef = React.useRef(new Map());

  const removeToast = React.useCallback((notificationId) => {
    setNotifications((currentNotifications) =>
      currentNotifications.filter(
        (notification) => notification.notificationId !== notificationId
      )
    );

    const timerId = autoCloseTimersRef.current.get(notificationId);
    if (timerId) {
      window.clearTimeout(timerId);
      autoCloseTimersRef.current.delete(notificationId);
    }
  }, []);

  const closeToast = React.useCallback(
    (notificationId) => {
      markNotificationRead(notificationId);
      removeToast(notificationId);
    },
    [removeToast]
  );

  const handleNotificationAction = React.useCallback(
    (notification, event) => {
      const callback = resolveNotificationCallback(notification.callBack);
      const anchor =
        event?.target instanceof Element ? event.target.closest('a') : null;

      if (anchor) {
        event.preventDefault();
      }

      if (event && !anchor) return;
      if (!callback) return;

      callback();
      markNotificationRead(notification.notificationId);
    },
    []
  );

  const handleIncomingNotification = React.useCallback((notification) => {
    if (!notification) return;

    setNotifications((currentNotifications) =>
      updateNotificationsState(currentNotifications, notification)
    );
  }, []);

  React.useEffect(() => {
    const onApplicationNotification = (event) => {
      handleIncomingNotification(event.detail);
    };

    document.addEventListener(
      EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
      onApplicationNotification
    );

    return () => {
      document.removeEventListener(
        EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
        onApplicationNotification
      );
    };
  }, [handleIncomingNotification]);

  React.useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;

    const onServiceWorkerMessage = (event) => {
      if (event.data && event.data.type === SERVICE_WORKER_RELOAD_EVENT) {
        window.location.reload();
      }

      if (event.data && event.data.type === EVENTS_DATA_TYPES.SYNC_DB_LS) {
        const activityModule = event.data?.notification;
        const moduleName = activityModule?.moduleName;

        if (moduleName === APP_CONFIG) {
          const subModules = activityModule?.subModules ?? [];
          subModules.forEach((element) => {
            syncStorage(element);
          });
        }
      }

      if (event.data && event.data.type === EVENTS_DATA_TYPES.SYNC_DB_SS) {
        const activityModule = event.data?.notification;
        const moduleName = activityModule?.moduleName;

        if (moduleName === AUTH_TOKENS) {
          const subModules = activityModule?.subModules ?? [];
          subModules.forEach((element) => {
            syncSessionStorage(element);
          });
        }
      }

      if (
        event.data &&
        (event.data.type === EVENTS_DATA_TYPES.PROCESS_NOTIFICATION ||
          event.data.type === EVENTS_DATA_TYPES.DELAY_NOTIFICATION)
      ) {
        handleIncomingNotification(event.data.notification);
      }

      if (
        event.data &&
        event.data.type === EVENTS_DATA_TYPES.UPDATE_SERVICE_WORKER
      ) {
        navigator.serviceWorker.getRegistrations().then((registrations) =>
          registrations.forEach((registration) => {
            registration.update();
          })
        );
      }
    };

    navigator.serviceWorker.addEventListener('message', onServiceWorkerMessage);

    return () => {
      navigator.serviceWorker.removeEventListener(
        'message',
        onServiceWorkerMessage
      );
    };
  }, [handleIncomingNotification]);

  React.useEffect(() => {
    const visibleNotifications = notifications.filter(
      (notification) =>
        notification.isNotify &&
        TOAST_VISIBILITY.has(notification.display ?? DisplayNotification.SHOW)
    );

    visibleNotifications.forEach((notification) => {
      if (
        notification.timeout > 0 &&
        !autoCloseTimersRef.current.has(notification.notificationId)
      ) {
        const timerId = window.setTimeout(() => {
          closeToast(notification.notificationId);
        }, notification.timeout);

        autoCloseTimersRef.current.set(notification.notificationId, timerId);
      }
    });

    return () => {
      autoCloseTimersRef.current.forEach((timerId, notificationId) => {
        const stillExists = visibleNotifications.some(
          (notification) => notification.notificationId === notificationId
        );

        if (!stillExists) {
          window.clearTimeout(timerId);
          autoCloseTimersRef.current.delete(notificationId);
        }
      });
    };
  }, [closeToast, notifications]);

  React.useEffect(
    () => () => {
      autoCloseTimersRef.current.forEach((timerId) => {
        window.clearTimeout(timerId);
      });
      autoCloseTimersRef.current.clear();
    },
    []
  );

  const visibleNotifications = notifications
    .filter(
      (notification, index, allNotifications) =>
        notification.isNotify &&
        TOAST_VISIBILITY.has(notification.display ?? DisplayNotification.SHOW) &&
        index ===
          allNotifications.findIndex(
            (item) =>
              item.notificationId === notification.notificationId && item.isNotify
          )
    )
    .slice()
    .reverse();

  if (visibleNotifications.length === 0) {
    return null;
  }

  return (
    <div className="pointer-events-none fixed bottom-4 left-4 z-[9999] flex max-w-[calc(100vw-1.5rem)] flex-col-reverse gap-3 sm:max-w-sm">
      {visibleNotifications.map((notification) => {
        const theme =
          notificationTheme[notification.style] ?? notificationTheme.info;
        const Icon = theme.icon;
        const callback = resolveNotificationCallback(notification.callBack);
        const isInteractive = Boolean(callback);
        const title = notification.title ?? 'Notification';

        return (
          <section
            key={notification.notificationId}
            aria-live="polite"
            className={`pointer-events-auto w-[min(26rem,calc(100vw-1.5rem))] overflow-hidden rounded-2xl border bg-card/95 text-card-foreground shadow-[0_22px_45px_-28px_rgba(15,23,42,0.5),0_12px_24px_-18px_rgba(15,23,42,0.32)] backdrop-blur ${theme.accent}`}
          >
            <div className="flex items-start gap-3 p-4">
              <Icon className={theme.iconClassName} />

              <div className="min-w-0 flex-1">
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1 space-y-2">
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                      <h3 className="min-w-0 text-sm font-semibold leading-5 text-foreground">
                        {title}
                      </h3>
                      <span
                        className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-medium leading-4 ${theme.badge}`}
                      >
                        {(notification.style ?? 'info').toUpperCase()}
                      </span>
                      {notification.count > 0 ? (
                        <span className="inline-flex shrink-0 items-center rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium leading-4 text-muted-foreground">
                          {notification.count}
                        </span>
                      ) : null}
                    </div>

                    {notification.description ? (
                      <div
                        className="notification-richtext text-[13px] leading-6 text-muted-foreground [overflow-wrap:anywhere] [&_a]:font-medium [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2 hover:[&_a]:text-foreground"
                        onClick={(event) => handleNotificationAction(notification, event)}
                        dangerouslySetInnerHTML={{
                          __html: notification.description,
                        }}
                      />
                    ) : null}

                    {isInteractive ? (
                      <button
                        type="button"
                        onClick={() => handleNotificationAction(notification)}
                        className="inline-flex items-center gap-2 rounded-full border border-border bg-background/80 px-3 py-1.5 text-[12px] font-medium text-foreground transition hover:border-primary/40 hover:bg-primary/10"
                      >
                        Reload now
                        <RefreshCw className="h-3.5 w-3.5" />
                      </button>
                    ) : null}
                  </div>

                  <button
                    type="button"
                    aria-label={`Dismiss ${title}`}
                    onClick={() => closeToast(notification.notificationId)}
                    className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-muted-foreground transition hover:bg-muted hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default AppNotifications;
